import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import apiClient from '@/lib/api-client';
import {
    Gift, Plus, Search, X, Package, DollarSign,
    Tag, ArrowRight, Eye, Layers, Sparkles,
    ShoppingBag, RefreshCw, LayoutGrid, List,
    CheckCircle,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

type ViewMode = 'card' | 'table';

export function HampersPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const [search, setSearch] = useState('');
    const [viewMode, setViewMode] = useState<ViewMode>('card');

    const { data: hampers, isLoading, refetch, isFetching } = useQuery({
        queryKey: ['hampers', user?.tenantId],
        queryFn: async () => {
            const { data } = await apiClient.get(`/tenants/${user!.tenantId}/hampers`);
            return data?.data || [];
        },
        enabled: !!user?.tenantId,
    });

    const hampersList = (hampers || []) as any[];

    const filteredHampers = hampersList.filter((h: any) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (h.name || '').toLowerCase().includes(q) ||
            (h.slug || '').toLowerCase().includes(q) ||
            (h.description || '').toLowerCase().includes(q);
    });

    const formatNaira = (val: string) => `₦${parseFloat(val || '0').toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

    const activeHampers = hampersList.filter((h: any) => h.isActive).length;
    const clearanceHampers = hampersList.filter((h: any) => h.isClearance).length;
    const totalItems = hampersList.reduce((sum: number, h: any) => sum + (h.items?.length || 0), 0);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-4">
                            <div className="p-2.5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-lg shadow-amber-500/25">
                                <Gift className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">Hampers & Bundles</h1>
                                <p className="text-sm text-slate-500">
                                    {hampersList.length} hampers · {activeHampers} active · {clearanceHampers} on clearance · {totalItems} total items
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => refetch()} disabled={isFetching}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button">
                                <RefreshCw className={clsx('w-4 h-4', isFetching && 'animate-spin')} /> Refresh
                            </button>
                            <div className="flex items-center bg-slate-100 rounded-xl p-0.5">
                                <button onClick={() => setViewMode('card')}
                                    className={clsx('p-1.5 rounded-lg transition-all', viewMode === 'card' ? 'bg-white shadow-sm text-amber-600' : 'text-slate-400')}
                                    type="button"><LayoutGrid size={16} /></button>
                                <button onClick={() => setViewMode('table')}
                                    className={clsx('p-1.5 rounded-lg transition-all', viewMode === 'table' ? 'bg-white shadow-sm text-amber-600' : 'text-slate-400')}
                                    type="button"><List size={16} /></button>
                            </div>
                            <button onClick={() => navigate('/hampers/new')}
                                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl hover:from-amber-600 hover:to-orange-700 transition-all font-semibold shadow-lg shadow-amber-500/25 text-sm"
                                type="button">
                                <Plus className="w-4 h-4" /> Create Hamper
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <QuickStat label="Total Hampers" value={hampersList.length} icon={Gift} gradient="from-amber-500 to-orange-600" />
                    <QuickStat label="Active" value={activeHampers} icon={CheckCircle} gradient="from-emerald-500 to-teal-600" />
                    <QuickStat label="On Clearance" value={clearanceHampers} icon={Tag} gradient="from-rose-500 to-pink-600" />
                    <QuickStat label="Total Items" value={totalItems} icon={Package} gradient="from-blue-500 to-indigo-600" />
                </div>

                {/* Search */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input type="text" placeholder="Search hampers by name or description..." value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 focus:bg-white transition-all text-sm" />
                        {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-slate-400" type="button"><X className="w-4 h-4" /></button>}
                    </div>
                </div>

                {/* Content */}
                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : filteredHampers.length === 0 ? (
                    <EmptyState
                        icon={<Gift size={48} />}
                        title={search ? "No matching hampers" : "No hampers yet"}
                        description={search ? "Try adjusting your search." : "Create gift hampers by bundling products together with attractive pricing."}
                        action={!search ? { label: 'Create First Hamper', onClick: () => navigate('/hampers/new') } : undefined}
                    />
                ) : viewMode === 'card' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {filteredHampers.map((hamper: any, idx: number) => {
                            const profit = parseFloat(hamper.sellingPrice || '0') - parseFloat(hamper.totalCost || '0');
                            const margin = parseFloat(hamper.sellingPrice) > 0 ? (profit / parseFloat(hamper.sellingPrice)) * 100 : 0;

                            return (
                                <motion.div
                                    key={hamper.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.03 }}
                                    whileHover={{ y: -5 }}
                                    onClick={() => navigate(`/hampers/${hamper.id}`)}
                                    className="group bg-white rounded-2xl border-2 border-slate-200 shadow-sm hover:shadow-xl hover:border-amber-300 transition-all duration-300 cursor-pointer overflow-hidden"
                                >
                                    {/* Top Gradient Bar */}
                                    <div className={clsx(
                                        'h-1.5',
                                        hamper.isClearance ? 'bg-gradient-to-r from-rose-500 to-pink-600' : 'bg-gradient-to-r from-amber-500 to-orange-600'
                                    )} />

                                    <div className="p-5">
                                        {/* Header */}
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className={clsx(
                                                    'w-12 h-12 rounded-xl flex items-center justify-center shadow-md',
                                                    hamper.isClearance ? 'bg-gradient-to-br from-rose-500 to-pink-600' : 'bg-gradient-to-br from-amber-500 to-orange-600'
                                                )}>
                                                    <Gift size={24} className="text-white" />
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-slate-900 group-hover:text-amber-700 transition-colors">{hamper.name}</h3>
                                                    <p className="text-xs text-slate-400">{hamper.items?.length || 0} items</p>
                                                </div>
                                            </div>
                                            <div className="flex flex-col gap-1 items-end">
                                                <Badge variant={hamper.isActive ? 'success' : 'danger'} size="sm">{hamper.isActive ? 'Active' : 'Inactive'}</Badge>
                                                {hamper.isClearance && <Badge variant="warning" size="sm">Clearance</Badge>}
                                            </div>
                                        </div>

                                        {/* Description */}
                                        {hamper.description && (
                                            <p className="text-xs text-slate-500 line-clamp-2 mb-3">{hamper.description}</p>
                                        )}

                                        {/* Pricing Grid */}
                                        <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-slate-100">
                                            <div className="text-center">
                                                <p className="text-[10px] text-slate-400 uppercase">Cost</p>
                                                <p className="text-sm font-bold text-slate-700">{formatNaira(hamper.totalCost)}</p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-[10px] text-slate-400 uppercase">Price</p>
                                                <p className="text-sm font-bold text-emerald-600">{formatNaira(hamper.sellingPrice)}</p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-[10px] text-slate-400 uppercase">Margin</p>
                                                <p className={clsx('text-sm font-bold', margin >= 0 ? 'text-emerald-600' : 'text-rose-600')}>
                                                    {margin.toFixed(0)}%
                                                </p>
                                            </div>
                                        </div>

                                        {/* Items Preview */}
                                        <div className="mt-3 pt-3 border-t border-slate-100">
                                            <div className="flex flex-wrap gap-1">
                                                {hamper.items?.slice(0, 4).map((item: any) => (
                                                    <span key={item.id} className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-[10px] font-medium">
                                                        {item.componentName || 'Item'} ×{item.quantity}
                                                    </span>
                                                ))}
                                                {hamper.items?.length > 4 && (
                                                    <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-[10px] font-medium">
                                                        +{hamper.items.length - 4} more
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* View Details */}
                                        <div className="flex items-center gap-1 mt-3 text-xs font-medium text-amber-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                            View Details <ArrowRight size={12} />
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                ) : (
                    <Card className="overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead><tr className="bg-slate-50/50">
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Hamper</th>
                                    <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Items</th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Cost</th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Price</th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Profit</th>
                                    <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                                </tr></thead>
                                <tbody className="divide-y divide-slate-50">
                                    {filteredHampers.map((hamper: any) => {
                                        const profit = parseFloat(hamper.sellingPrice || '0') - parseFloat(hamper.totalCost || '0');
                                        return (
                                            <tr key={hamper.id} onClick={() => navigate(`/hampers/${hamper.id}`)}
                                                className="group hover:bg-slate-50 cursor-pointer transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                                                            <Gift size={16} className="text-white" />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-semibold text-slate-900">{hamper.name}</p>
                                                            <p className="text-xs text-slate-400">{hamper.slug}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center text-sm">{hamper.items?.length || 0}</td>
                                                <td className="px-6 py-4 text-right text-sm">{formatNaira(hamper.totalCost)}</td>
                                                <td className="px-6 py-4 text-right text-sm font-semibold text-emerald-600">{formatNaira(hamper.sellingPrice)}</td>
                                                <td className="px-6 py-4 text-right text-sm font-semibold">{formatNaira(String(profit))}</td>
                                                <td className="px-6 py-4 text-center">
                                                    <Badge variant={hamper.isActive ? 'success' : 'danger'} size="sm">{hamper.isActive ? 'Active' : 'Inactive'}</Badge>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}
            </div>
        </div>
    );
}

function QuickStat({ label, value, icon: Icon, gradient }: { label: string; value: number; icon: React.ElementType; gradient: string }) {
    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-lg transition-all">
            <div className="flex items-start justify-between">
                <div className="space-y-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{value.toLocaleString()}</p>
                </div>
                <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient} shadow-lg`}>
                    <Icon className="w-5 h-5 text-white" />
                </div>
            </div>
        </motion.div>
    );
}